import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  FileText,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Settings,
  X
} from 'lucide-react';
import { formatCurrency, formatDate, subscriptionAPI, bondsAPI } from '../../services/api';
import {
  SUBSCRIPTION_STATUS,
  BOND_STATUS,
  BOND_TYPE,
  BOND_TYPE_ICONS
} from '../../constants/subscription.constants';

const SubscriptionManagement = () => {
  const { user: authUser } = useAuth() || {};
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [issuerFilter, setIssuerFilter] = useState('all');
  const [showSetPeriodModal, setShowSetPeriodModal] = useState(false);
  const [selectedBondForPeriod, setSelectedBondForPeriod] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [subscriptionDates, setSubscriptionDates] = useState({
    startDate: '',
    endDate: ''
  });
  const [dateValidationErrors, setDateValidationErrors] = useState({
    startDate: '',
    endDate: ''
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBondForDetails, setSelectedBondForDetails] = useState(null);
  const [allBonds, setAllBonds] = useState([]); // Combined bonds from single API call
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodApprovals, setPeriodApprovals] = useState({});
  const [periodDrafts, setPeriodDrafts] = useState({}); // bondId -> { startDate, endDate }
  const [notification, setNotification] = useState({ open: false, title: '', message: '', type: 'info' });
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const showNotification = (message, opts = {}) => {
    const { title = 'Notice', type = 'info' } = opts;
    setNotification({ open: true, title, message, type });
  };

  const showToast = (message, type = 'success') => {
    setToast({ open: true, message, type });
    setTimeout(() => setToast({ open: false, message: '', type: 'success' }), 3000);
  };

  // Target statuses for bonds to display
  const TARGET_STATUSES = useMemo(() => [
    BOND_STATUS.PENDING_REVIEW, // issuer-approved/pending FA review can set subscription here first
    BOND_STATUS.APPROVED,       // keep showing already approved bonds as well
    SUBSCRIPTION_STATUS.READY_FOR_ALLOCATION,
    SUBSCRIPTION_STATUS.ALLOCATION_UPLOADED,
    SUBSCRIPTION_STATUS.ALLOCATION_FINALIZED
  ], []);

  // Single consolidated API call to fetch all bonds with approval data
  const fetchAllBondsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Single API call to get bonds with their approval states
      const response = await bondsAPI.getBonds({
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        // Include all statuses we care about
        status: TARGET_STATUSES.join(',')
      });

      const payload = response?.data?.data;
      const bonds = payload?.bonds || payload?.data || [];
      const totalPages = payload?.totalPages || 1;
      const totalItems = payload?.total || bonds.length;

      // Process bonds data
      const isPendingReview = (status) => {
        if (!status) return false;
        const s = String(status).toLowerCase().trim();
        return s === 'pending review' || s === 'pending_review' || s === 'pendingreview';
      };

      const mappedBonds = bonds.map(bond => ({
        id: bond._id,
        bondName: bond.bondName,
        issuer: bond.issuer?.corporateInfo?.companyName || bond.issuer?.email || 'Unknown Issuer',
        isin: bond.isin,
        stockCode: bond.stockCode,
        status: isPendingReview(bond.status) ? BOND_STATUS.PENDING_REVIEW : bond.status,
        bondType: /sukuk|islamic/i.test(String(bond.productType || '')) ? BOND_TYPE.ISLAMIC : BOND_TYPE.CONVENTIONAL,
        couponRate: bond.couponRate,
        profitRate: bond.couponRate,
        maturityDate: bond.maturityDate,
        issueDate: bond.issueDate,
        totalNominalValue: bond.issueAmount,
        currency: bond.currency || 'MYR',
        subscriptionStartDate: bond.subscriptionStartDate,
        subscriptionEndDate: bond.subscriptionEndDate,
        approvalDate: bond.approvalDate,
        minSubscription: bond.minSubscription,
        bondNumber: bond.bondNumber,
        approvalNotes: bond.approvalNotes
      }));

      setAllBonds(mappedBonds);
      setPagination({ totalPages, totalItems });

      // Fetch approval states for all bonds in one batch
      if (mappedBonds.length > 0) {
        const bondIds = mappedBonds.map(b => b.id);
        try {
          const res = await subscriptionAPI.getSubscriptionPeriodApprovals(bondIds);
          if (res.data.success && res.data.data) {
            setPeriodApprovals(res.data.data);
            // Also update periodDrafts from l1Payload
            const newDrafts = {};
            Object.entries(res.data.data).forEach(([bondId, approval]) => {
              if (approval && approval.l1Payload && approval.l1Payload.startDate && approval.l1Payload.endDate) {
                newDrafts[bondId] = {
                  startDate: approval.l1Payload.startDate,
                  endDate: approval.l1Payload.endDate
                };
              }
            });
            setPeriodDrafts(newDrafts);
          }
        } catch (e) {
          // Silent failure for approvals, table will show placeholders
          console.warn('Failed to fetch approval states:', e);
        }
      }

    } catch (error) {
      console.error('Error fetching bonds data:', error);
      setError(error.message);
      setAllBonds([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    fetchAllBondsData();
  }, [refreshTrigger, page, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use bonds from single API call
  const subscriptions = useMemo(() => {
    return allBonds;
  }, [allBonds]);

  // Get unique issuers for filter options
  const uniqueIssuers = [...new Set(subscriptions.map(sub => sub.issuer))];

  // Filter and search logic
  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter(subscription => {
      const matchesSearch =
        subscription.bondName.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (subscription.isin && subscription.isin.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        (subscription.stockCode && subscription.stockCode.toLowerCase().includes(debouncedSearchTerm.toLowerCase())) ||
        subscription.issuer.toLowerCase().includes(debouncedSearchTerm.toLowerCase());

      // Allow Pending Review items from issuer list even if the subscription endpoint would normally exclude them
      const statusAllowed = TARGET_STATUSES.includes(subscription.status) || !!subscription.__fromBonds;
      const matchesStatus = statusFilter === 'all'
        ? statusAllowed
        : subscription.status === statusFilter;
      const matchesIssuer = issuerFilter === 'all' || subscription.issuer === issuerFilter;

      return matchesSearch && matchesStatus && matchesIssuer;
    });
  }, [subscriptions, debouncedSearchTerm, statusFilter, issuerFilter, TARGET_STATUSES]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      [BOND_STATUS.PENDING_REVIEW]: { color: 'yellow', icon: Settings, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      [BOND_STATUS.APPROVED]: { color: 'green', icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
      [SUBSCRIPTION_STATUS.UPCOMING]: { color: 'blue', icon: CheckCircle, bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      [SUBSCRIPTION_STATUS.ACTIVE]: { color: 'green', icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
      [SUBSCRIPTION_STATUS.CLOSED]: { color: 'gray', icon: XCircle, bgColor: 'bg-gray-100', textColor: 'text-gray-800' },
      [SUBSCRIPTION_STATUS.ALLOCATED]: { color: 'purple', icon: CheckCircle, bgColor: 'bg-purple-100', textColor: 'text-purple-800' },
      [SUBSCRIPTION_STATUS.APPROVED_FOR_SUBSCRIPTION]: { color: 'yellow', icon: Settings, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      [SUBSCRIPTION_STATUS.ALLOCATING_SUBSCRIBERS]: { color: 'orange', icon: Settings, bgColor: 'bg-orange-100', textColor: 'text-orange-800' },
      [SUBSCRIPTION_STATUS.READY_FOR_ALLOCATION]: { color: 'indigo', icon: CheckCircle, bgColor: 'bg-indigo-100', textColor: 'text-indigo-800' },
      [SUBSCRIPTION_STATUS.ALLOCATION_UPLOADED]: { color: 'green', icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
      [SUBSCRIPTION_STATUS.ALLOCATION_FINALIZED]: { color: 'purple', icon: CheckCircle, bgColor: 'bg-purple-100', textColor: 'text-purple-800' }
    };

    const config = statusConfig[status] || statusConfig[SUBSCRIPTION_STATUS.CLOSED];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getBondTypeIcon = (bondType) => {
    return BOND_TYPE_ICONS[bondType] || BOND_TYPE_ICONS[BOND_TYPE.CONVENTIONAL];
  };

  // Validate subscription dates against issue date
  const validateSubscriptionDates = (startDateStr, endDateStr, issueDate) => {
    const errors = { startDate: '', endDate: '' };

    if (!issueDate) return errors;

    // Convert issue date from UTC to Malaysia timezone (UTC+8)
    // Issue date is stored in UTC but represents Malaysia midnight
    const issueDateObj = new Date(issueDate);
    const issueDateMalaysia = new Date(issueDateObj.getTime() + (8 * 60 * 60 * 1000));
    issueDateMalaysia.setUTCHours(0, 0, 0, 0);

    if (startDateStr) {
      const startDateObj = new Date(startDateStr + 'T00:00:00');
      startDateObj.setHours(0, 0, 0, 0);
      if (startDateObj >= issueDateMalaysia) {
        errors.startDate = `Start date must be before issue date (${formatDate(issueDate)})`;
      }
    }

    if (endDateStr) {
      const endDateObj = new Date(endDateStr + 'T00:00:00');
      endDateObj.setHours(0, 0, 0, 0);
      if (endDateObj >= issueDateMalaysia) {
        errors.endDate = `End date must be before issue date (${formatDate(issueDate)})`;
      }
    }

    return errors;
  };

  const handleSetSubscriptionPeriod = (bond, editExisting = false) => {
    setSelectedBondForPeriod(bond);
    setDateValidationErrors({ startDate: '', endDate: '' });
    let startDate = '';
    let endDate = '';
    // Helper to format ISO string to 'YYYY-MM-DD' for date input
    const toDateLocal = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      const pad = n => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    if (editExisting) {
      // Prefer periodApprovals.l1Payload if available (survives refresh), else fallback to periodDrafts
      const approval = periodApprovals[bond.id];
      if (approval && approval.l1Payload && approval.l1Payload.startDate && approval.l1Payload.endDate) {
        startDate = toDateLocal(approval.l1Payload.startDate);
        endDate = toDateLocal(approval.l1Payload.endDate);
      } else if (periodDrafts[bond.id]) {
        startDate = toDateLocal(periodDrafts[bond.id].startDate);
        endDate = toDateLocal(periodDrafts[bond.id].endDate);
      }
    }
    setSubscriptionDates({ startDate, endDate });
    setShowSetPeriodModal(true);
  };

  const handleSavePeriodDraft = () => {
    if (!subscriptionDates.startDate || !subscriptionDates.endDate) {
      showNotification('Please select both start and end dates.', { title: 'Validation Error', type: 'error' });
      return;
    }
    const startDate = new Date(subscriptionDates.startDate);
    const endDate = new Date(subscriptionDates.endDate);
    if (startDate >= endDate) {
      showNotification('End date must be after start date.', { title: 'Validation Error', type: 'error' });
      return;
    }

    // Check if start date is in the past in KL timezone (UTC+8)
    const now = new Date();
    const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
    const nowKL = new Date(nowUTC + (8 * 60 * 60 * 1000));
    const todayKL = new Date(Date.UTC(nowKL.getUTCFullYear(), nowKL.getUTCMonth(), nowKL.getUTCDate()));

    // Parse the start date string and create UTC date
    const startDateParts = subscriptionDates.startDate.split('-');
    const startDateUTC = new Date(Date.UTC(parseInt(startDateParts[0]), parseInt(startDateParts[1]) - 1, parseInt(startDateParts[2])));

    if (startDateUTC < todayKL) {
      showNotification('Start date cannot be in the past (KL time).', { title: 'Validation Error', type: 'error' });
      return;
    }
    // Validate subscription dates are before issue date
    if (selectedBondForPeriod?.issueDate) {
      // Convert issue date from UTC to Malaysia timezone for proper comparison
      const issueDateObj = new Date(selectedBondForPeriod.issueDate);
      const issueDateMalaysia = new Date(issueDateObj.getTime() + (8 * 60 * 60 * 1000));
      issueDateMalaysia.setUTCHours(0, 0, 0, 0);

      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);

      if (startDateOnly >= issueDateMalaysia) {
        showNotification('Subscription start date must be before the issue date.', { title: 'Validation Error', type: 'error' });
        return;
      }
      if (endDateOnly >= issueDateMalaysia) {
        showNotification('Subscription end date must be before the issue date.', { title: 'Validation Error', type: 'error' });
        return;
      }
    }
    // Persist draft to backend
    (async () => {
      try {
        // If the bond isn't registered in subscription module yet, register it first
        if (selectedBondForPeriod?.__fromBonds) {
          try {
            await subscriptionAPI.addSubscriptionBond({
              id: selectedBondForPeriod.id,
              bondName: selectedBondForPeriod.bondName,
              isin: selectedBondForPeriod.isin,
              stockCode: selectedBondForPeriod.stockCode,
              issuer: selectedBondForPeriod.issuer,
              totalNominalValue: selectedBondForPeriod.totalNominalValue,
              bondType: selectedBondForPeriod.bondType,
              couponRate: selectedBondForPeriod.couponRate,
              profitRate: selectedBondForPeriod.profitRate,
              maturityDate: selectedBondForPeriod.maturityDate,
              issueDate: selectedBondForPeriod.issueDate,
              currency: selectedBondForPeriod.currency || 'MYR',
            });
          } catch (e) {
            // If it already exists, proceed; otherwise surface error
            // no-op
          }
        }
        const res = await subscriptionAPI.setSubscriptionPeriod(selectedBondForPeriod.id, {
          startDate: subscriptionDates.startDate,
          endDate: subscriptionDates.endDate
        });
        if (res.data.success) {
          setPeriodDrafts(prev => ({
            ...prev,
            [selectedBondForPeriod.id]: {
              startDate: subscriptionDates.startDate,
              endDate: subscriptionDates.endDate
            }
          }));
          setShowSetPeriodModal(false);
          setSelectedBondForPeriod(null);
          setSubscriptionDates({ startDate: '', endDate: '' });
          showNotification('Draft saved and persisted. You can still edit before L1 approval.', { title: 'Draft Saved', type: 'success' });
        } else {
          showNotification(res.data.message || 'Failed to save draft to backend.', { title: 'Save Failed', type: 'error' });
        }
      } catch (error) {
        console.error('Error saving draft period:', error);
        showNotification(error.response?.data?.message || error.message || 'Failed to save draft to backend.', { title: 'Save Failed', type: 'error' });
      }
    })();
  };

  const handleApproveL1 = (bond) => {
    const draft = periodDrafts[bond.id];
    if (!draft) { showNotification('Please set a subscription period first.', { title: 'Action Required', type: 'warning' }); return; }

    // Validate that the draft dates are valid before approving
    if (!draft.startDate || !draft.endDate) {
      showNotification('Please ensure both start and end dates are set.', { title: 'Validation Error', type: 'error' });
      return;
    }

    const startDate = new Date(draft.startDate);
    const endDate = new Date(draft.endDate);
    if (startDate >= endDate) {
      showNotification('End date must be after start date.', { title: 'Validation Error', type: 'error' });
      return;
    }

    // Validate subscription dates are before issue date
    if (bond?.issueDate) {
      const issueDateObj = new Date(bond.issueDate);
      const issueDateMalaysia = new Date(issueDateObj.getTime() + (8 * 60 * 60 * 1000));
      issueDateMalaysia.setUTCHours(0, 0, 0, 0);

      const startDateOnly = new Date(startDate);
      startDateOnly.setHours(0, 0, 0, 0);
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);

      if (startDateOnly >= issueDateMalaysia) {
        showNotification('Subscription start date must be before the issue date.', { title: 'Validation Error', type: 'error' });
        return;
      }
      if (endDateOnly >= issueDateMalaysia) {
        showNotification('Subscription end date must be before the issue date.', { title: 'Validation Error', type: 'error' });
        return;
      }
    }

    setConfirmAction({
      type: 'L1',
      bond,
      title: 'Confirm L1 Approval',
      message: `Are you sure you want to approve L1 for "${bond.bondName}"? This will lock the subscription period and require L2 approval from another user.`,
      onConfirm: async () => {
        try {
          const res = await subscriptionAPI.approveSubscriptionPeriod(bond.id, { level: 'L1', startDate: draft.startDate, endDate: draft.endDate });
          if (res.data.success) {
            showToast('L1 approval recorded successfully.', 'success');
            setPeriodApprovals(prev => ({ ...prev, [bond.id]: res.data.data }));
            setPeriodDrafts(prev => { const clone = { ...prev }; delete clone[bond.id]; return clone; });
          }
        } catch (error) {
          console.error('Error approving L1:', error);
          showNotification(error.response?.data?.message || error.message, { title: 'L1 Approval Failed', type: 'error' });
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleApproveL2 = (bond, approvalState) => {
    if (approvalState.l1ApprovedBy === authUser?._id) {
      showNotification('You performed L1. A different user must perform L2 approval.', { title: 'Cannot Approve L2', type: 'warning' });
      return;
    }
    setConfirmAction({
      type: 'L2',
      bond,
      title: 'Confirm L2 Approval',
      message: `Are you sure you want to approve L2 for "${bond.bondName}"? This will finalize the subscription period and move the bond to Ready for Allocation.`,
      onConfirm: async () => {
        try {
          const res = await subscriptionAPI.approveSubscriptionPeriod(bond.id, { level: 'L2' });
          if (res.data.success) {
            showToast('L2 approval complete.', 'success');
            setPeriodApprovals(prev => ({ ...prev, [bond.id]: res.data.data }));
            setRefreshTrigger(p => p + 1);
          }
        } catch (error) {
          console.error('Error approving L2:', error);
          showNotification(error.response?.data?.message || error.message, { title: 'L2 Approval Failed', type: 'error' });
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleViewDetails = (subscription) => {
    setSelectedBondForDetails(subscription);
    setShowDetailsModal(true);
  };

  const getActionButton = (subscription) => {
    // For Active bonds, show subscription period and disable the button
    if (subscription.status === SUBSCRIPTION_STATUS.ACTIVE) {
      return (
        <div className="flex space-x-2">
          <button
            disabled
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-400 bg-gray-100 cursor-not-allowed"
          >
            <Settings className="w-3 h-3 mr-1" />
            Period Set
          </button>
          <button
            onClick={() => handleViewDetails(subscription)}

            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    // For issuer-approved or pending-review bonds that need subscription period setup
    if (subscription.status === BOND_STATUS.APPROVED || subscription.status === BOND_STATUS.PENDING_REVIEW) {
      const approvalState = periodApprovals[subscription.id];
      const draft = periodDrafts[subscription.id];
      const pendingL2 = approvalState && approvalState.status === 'Pending L2';
      // L1 not yet approved
      if (!approvalState || approvalState.status === 'Pending L1') {
        return (
          <div className="flex space-x-2">
            {!draft && (
              <button
                onClick={() => handleSetSubscriptionPeriod(subscription)}

                className="btn-primary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Settings className="w-3 h-3 mr-1" />
                Set Subscription Period
              </button>
            )}
            {draft && !approvalState?.l1ApprovedBy && (
              <>
                <button
                  onClick={() => handleApproveL1(subscription)}

                  className="btn-primary btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approve L1
                </button>
                <button
                  onClick={() => handleSetSubscriptionPeriod(subscription, true)}

                  className="p-1 text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Edit Period"
                  style={{ color: '#24295f' }}
                >
                  Edit Period
                </button>
              </>
            )}
            <button
              onClick={() => handleViewDetails(subscription)}

              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Eye className="w-3 h-3 mr-1" />
              View Details
            </button>
          </div>
        );
      }
      // Pending L2
      if (pendingL2) {
        // Check if current user was the one who approved L1
        const l1ApprovedBy = approvalState.l1ApprovedBy;
        const currentUserId = authUser?._id || authUser?.id;
        const currentUserApprovedL1 = l1ApprovedBy && currentUserId && String(l1ApprovedBy) === String(currentUserId);

        return (
          <div className="flex space-x-2">
            {!currentUserApprovedL1 && (
              <button
                onClick={() => handleApproveL2(subscription, approvalState)}

                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Approve L2
              </button>
            )}
            <button
              onClick={() => handleViewDetails(subscription)}

              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Eye className="w-3 h-3 mr-1" />
              View Details
            </button>
          </div>
        );
      }
    }
    if (subscription.status === SUBSCRIPTION_STATUS.APPROVED_FOR_SUBSCRIPTION ||
      subscription.status === SUBSCRIPTION_STATUS.ALLOCATING_SUBSCRIBERS) {
      return (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewDetails(subscription)}

            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    // For bonds that are ready for allocation or have completed allocation
    if (subscription.status === SUBSCRIPTION_STATUS.READY_FOR_ALLOCATION ||
      subscription.status === SUBSCRIPTION_STATUS.ALLOCATION_UPLOADED ||
      subscription.status === SUBSCRIPTION_STATUS.ALLOCATION_FINALIZED) {
      return (
        <div className="flex space-x-2">
          <button
            disabled
            className="btn-secondary btn-sm"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            {subscription.status === SUBSCRIPTION_STATUS.READY_FOR_ALLOCATION ? 'Ready for Allocation' :
              subscription.status === SUBSCRIPTION_STATUS.ALLOCATION_UPLOADED ? 'Allocation Complete' :
                'Finalized'}
          </button>
          <button
            onClick={() => handleViewDetails(subscription)}

            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Eye className="w-3 h-3 mr-1" />
            View Details
          </button>
        </div>
      );
    }

    // For other statuses, only show view details
    return (
      <button
        onClick={() => handleViewDetails(subscription)}

        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Eye className="w-3 h-3 mr-1" />
        View Details
      </button>
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setIssuerFilter('all');
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Subscription Management</h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage bond subscription periods
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Filters and Search */}
        <div className="grid grid-cols-1 gap-4 mb-6 lg:grid-cols-5">
          {/* Search Card */}
          <div className="lg:col-span-3">
            <div className="card">
              <div className="card-body">
                <div className="relative">
                  <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                  <input
                    type="text"
                    placeholder="Search bonds by name, ISIN, or issuer..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status Filter Card */}
          <div className="lg:col-span-1">
            <div className="h-full card">
              <div className="flex items-center h-full card-body">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value={BOND_STATUS.PENDING_REVIEW}>{BOND_STATUS.PENDING_REVIEW}</option>
                  <option value={BOND_STATUS.APPROVED}>{BOND_STATUS.APPROVED}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Issuer Filter Card */}
          <div className="lg:col-span-1">
            <div className="h-full card">
              <div className="flex items-center h-full card-body">
                <select
                  value={issuerFilter}
                  onChange={(e) => setIssuerFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Issuers</option>
                  {uniqueIssuers.map(issuer => (
                    <option key={issuer} value={issuer}>{issuer}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Page {page} of {pagination.totalPages} • Showing {filteredSubscriptions.length} of {pagination.totalItems || subscriptions.length} bonds
          </p>
        </div>

        {/* Subscription List Table */}
        <div className="overflow-hidden overflow-x-auto bg-white rounded-lg shadow">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Subscription Management</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Bond Details</th>
                  <th className="table-header-cell">ISIN</th>
                  <th className="table-header-cell">Stock Code</th>
                  <th className="table-header-cell">Issuer</th>
                  <th className="table-header-cell">Subscription Period</th>
                  <th className="table-header-cell">L1 Approval</th>
                  <th className="table-header-cell">L2 Approval</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 mb-4 border-b-2 rounded-full animate-spin border-primary-600"></div>
                        <p className="text-lg font-medium">Loading bonds...</p>
                        <p className="mt-1 text-sm text-gray-400">Please wait while we fetch the data</p>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-red-500">
                      <XCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
                      <p className="text-lg font-medium">Error loading bonds</p>
                      <p className="mt-1 text-sm text-gray-600">{error}</p>
                      <button
                        onClick={() => setRefreshTrigger(p => p + 1)}
                        className="mt-4 btn-primary btn-sm"
                      >
                        Retry
                      </button>
                    </td>
                  </tr>
                ) : filteredSubscriptions.length === 0 && !debouncedSearchTerm ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium">No subscription periods found</p>
                      <p className="text-sm">Try adjusting your search or filter criteria</p>
                    </td>
                  </tr>
                ) : filteredSubscriptions.length > 0 ? (
                  filteredSubscriptions.map((subscription) => {
                    const approvalState = periodApprovals[subscription.id];
                    return (
                      <tr key={subscription.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div className="text-sm font-medium text-gray-900">
                                {subscription.bondName}
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {subscription.bondType}
                              {/* Removed inline Approved date display as requested */}
                              {/* Removed inline L1/L2 approver display (now separate columns) */}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-mono text-sm text-gray-900">
                            {subscription.isin || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-mono text-sm text-gray-900">
                            {subscription.stockCode || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{subscription.issuer}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {subscription.subscriptionStartDate && subscription.subscriptionEndDate ? (
                              <>
                                <div className="text-sm text-gray-900">
                                  {formatDate(subscription.subscriptionStartDate)}
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                  to {formatDate(subscription.subscriptionEndDate)}
                                </div>
                              </>
                            ) : (
                              <div className="text-xs text-gray-500">
                                Not set
                              </div>
                            )}
                          </div>
                        </td>
                        {/* L1 Approval Column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {approvalState?.l1ApprovedBy ? (
                            <div className="text-xs text-gray-700">
                              <span className="block font-medium">
                                {approvalState.l1ApprovedBy === authUser?._id ? 'You' : (approvalState.l1ApprovedByEmail || 'Unknown')}
                              </span>
                              {approvalState.l1ApprovedAt && (
                                <span className="text-[10px] text-gray-500">{new Date(approvalState.l1ApprovedAt).toLocaleDateString()}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-400">—</span>
                          )}
                        </td>
                        {/* L2 Approval Column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {approvalState?.l2ApprovedBy ? (
                            <div className="text-xs text-gray-700">
                              <span className="block font-medium">
                                {approvalState.l2ApprovedBy === authUser?._id ? 'You' : (approvalState.l2ApprovedByEmail || 'Unknown')}
                              </span>
                              {approvalState.l2ApprovedAt && (
                                <span className="text-[10px] text-gray-500">{new Date(approvalState.l2ApprovedAt).toLocaleDateString()}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            {getStatusBadge(subscription.status)}
                            {/* Secondary approval status badge removed per request */}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap">
                          <div className="flex flex-col items-end space-y-2">
                            {getActionButton(subscription)}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : null}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <div className="flex items-center space-x-2 text-xs text-gray-600">
              <span>Total: {pagination.totalItems}</span>
              <label className="flex items-center space-x-1">
                <span>Per Page</span>
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="border-gray-300 rounded px-1 py-0.5 text-xs"
                >
                  {[10, 20, 50, 100].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2 py-1 text-xs border rounded disabled:opacity-40"
              >Prev</button>
              <span className="text-xs">{page} / {pagination.totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={page >= pagination.totalPages}
                className="px-2 py-1 text-xs border rounded disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* Set Subscription Period Modal */}
      {showSetPeriodModal && selectedBondForPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-2xl p-6 mx-4 bg-white rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Set Subscription Period
              </h3>
              <button
                onClick={() => setShowSetPeriodModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Bond Details */}
            <div className="p-4 mb-6 rounded-lg bg-gray-50">
              <h4 className="mb-2 text-sm font-medium text-gray-700">Bond Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Bond Name:</span>
                  <span className="ml-2 font-medium">{selectedBondForPeriod.bondName}</span>
                </div>
                <div>
                  <span className="text-gray-600">ISIN:</span>
                  <span className="ml-2 font-mono">{selectedBondForPeriod.isin}</span>
                </div>
                <div>
                  <span className="text-gray-600">Issuer:</span>
                  <span className="ml-2 font-medium">{selectedBondForPeriod.issuer}</span>
                </div>
                <div>
                  <span className="text-gray-600">Nominal Amount:</span>
                  <span className="ml-2 font-medium">{formatCurrency(selectedBondForPeriod.totalNominalValue)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Issue Date:</span>
                  <span className="ml-2 font-medium">{selectedBondForPeriod.issueDate ? formatDate(selectedBondForPeriod.issueDate) : 'Not set'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Maturity Date:</span>
                  <span className="ml-2 font-medium">{selectedBondForPeriod.maturityDate ? formatDate(selectedBondForPeriod.maturityDate) : 'Not set'}</span>
                </div>
              </div>
            </div>

            {/* Information Panel */}
            <div className="p-4 mb-6 border border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="mb-2 font-medium">Set Subscription Period</p>
                  <ul className="space-y-1 text-xs list-disc list-inside">
                    <li>This will activate the bond for investor subscription</li>
                    <li>Subscription period cannot be changed once set</li>
                    <li><strong>Both start and end dates must be before the issue date</strong></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Date Selection (Two-step approval aware) */}
            <div className="space-y-4">
              {(() => {
                const approvalState = periodApprovals[selectedBondForPeriod.id];
                const pendingL2 = approvalState && approvalState.status === 'Pending L2';
                const approved = approvalState && approvalState.status === 'Approved';
                const disableInputs = pendingL2 || approved;
                const proposedStart = approvalState?.l1Payload?.startDate;
                const proposedEnd = approvalState?.l1Payload?.endDate;

                // Convert issue date from UTC to Malaysia timezone for date picker max value
                let maxDateStr = undefined;
                if (selectedBondForPeriod?.issueDate) {
                  const issueDateObj = new Date(selectedBondForPeriod.issueDate);
                  // Add 8 hours to convert from UTC to Malaysia time
                  const issueDateMalaysia = new Date(issueDateObj.getTime() + (8 * 60 * 60 * 1000));
                  // Subtract 1 day to get the day before issue date
                  const maxDate = new Date(issueDateMalaysia.getTime() - 86400000);
                  maxDateStr = maxDate.toISOString().slice(0, 10);
                }

                return (
                  <>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Subscription Start Date * {pendingL2 && '(Proposed at L1)'}
                      </label>
                      <input
                        type="date"
                        value={disableInputs ? (proposedStart ? new Date(proposedStart).toISOString().slice(0, 10) : '') : subscriptionDates.startDate}
                        onChange={(e) => {
                          if (!disableInputs) {
                            const newStartDate = e.target.value;
                            setSubscriptionDates(prev => ({ ...prev, startDate: newStartDate }));
                            const errors = validateSubscriptionDates(newStartDate, subscriptionDates.endDate, selectedBondForPeriod?.issueDate);
                            setDateValidationErrors(errors);
                          }
                        }}
                        min={new Date().toISOString().slice(0, 10)}
                        max={maxDateStr}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${disableInputs ? 'bg-gray-100 cursor-not-allowed' : ''} ${dateValidationErrors.startDate ? 'border-red-500' : 'border-gray-300'}`}
                        required
                        disabled={disableInputs}
                      />
                      {dateValidationErrors.startDate && (
                        <p className="mt-1 text-xs font-medium text-red-600">{dateValidationErrors.startDate}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {pendingL2 ? 'Awaiting L2 approval. Start date locked.' : ''}
                      </p>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Subscription End Date * {pendingL2 && '(Proposed at L1)'}
                      </label>
                      <input
                        type="date"
                        value={disableInputs ? (proposedEnd ? new Date(proposedEnd).toISOString().slice(0, 10) : '') : subscriptionDates.endDate}
                        onChange={(e) => {
                          if (!disableInputs) {
                            const newEndDate = e.target.value;
                            setSubscriptionDates(prev => ({ ...prev, endDate: newEndDate }));
                            const errors = validateSubscriptionDates(subscriptionDates.startDate, newEndDate, selectedBondForPeriod?.issueDate);
                            setDateValidationErrors(errors);
                          }
                        }}
                        min={subscriptionDates.startDate || new Date().toISOString().slice(0, 10)}
                        max={maxDateStr}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${disableInputs ? 'bg-gray-100 cursor-not-allowed' : ''} ${dateValidationErrors.endDate ? 'border-red-500' : 'border-gray-300'}`}
                        required
                        disabled={disableInputs}
                      />
                      {dateValidationErrors.endDate && (
                        <p className="mt-1 text-xs font-medium text-red-600">{dateValidationErrors.endDate}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        {pendingL2 ? 'Awaiting L2 approval. End date locked.' : ''}
                      </p>
                    </div>
                    {!disableInputs && subscriptionDates.startDate && subscriptionDates.endDate && (
                      <div className="p-3 rounded-md bg-gray-50">
                        <p className="text-sm text-gray-600">
                          <strong>Subscription Duration:</strong> {' '}
                          {Math.ceil((new Date(subscriptionDates.endDate) - new Date(subscriptionDates.startDate)) / (1000 * 60 * 60 * 24))} days
                        </p>
                      </div>
                    )}
                    {pendingL2 && (
                      <div className="p-3 text-xs text-yellow-800 border border-yellow-200 rounded-md bg-yellow-50">
                        L1 approved. Another authorized user must perform L2 to finalize the period.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end mt-6 space-x-3">
              {(() => {
                const approvalState = periodApprovals[selectedBondForPeriod.id];
                const pendingL2 = approvalState && approvalState.status === 'Pending L2';
                const approved = approvalState && approvalState.status === 'Approved';
                const draft = periodDrafts[selectedBondForPeriod.id];
                const showSave = !pendingL2 && !approved; // Only allow draft save before L1 and while not locked
                const hasDateErrors = dateValidationErrors.startDate || dateValidationErrors.endDate;
                const saveDisabled = showSave && (!subscriptionDates.startDate || !subscriptionDates.endDate || hasDateErrors);
                return (
                  <>
                    <button
                      onClick={() => { setShowSetPeriodModal(false); setSelectedBondForPeriod(null); }}
                      className="btn-secondary"
                    >
                      {showSave ? 'Cancel' : 'Close'}
                    </button>
                    {showSave && (
                      <button
                        onClick={handleSavePeriodDraft}
                        disabled={saveDisabled}
                        className="btn-primary"
                      >
                        {draft ? 'Update Draft' : 'Save Draft'}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedBondForDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6 ">
              <h3 className="text-xl font-medium text-gray-900">
                Bond Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="pb-2 text-lg font-medium text-gray-900 border-b">Basic Information</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bond Name</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBondForDetails.bondName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ISIN</label>
                    <p className="mt-1 font-mono text-sm text-gray-900">{selectedBondForDetails.isin}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stock Code</label>
                    <p className="mt-1 font-mono text-sm text-gray-900">
                      {selectedBondForDetails.stockCode || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Issuer</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBondForDetails.issuer}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bond Type</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBondForDetails.bondType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nominal Amount</label>
                    <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedBondForDetails.totalNominalValue)}</p>
                  </div>
                </div>
              </div>

              {/* Status & Dates */}
              <div className="space-y-4">
                <h4 className="pb-2 text-lg font-medium text-gray-900 border-b">Status & Dates</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current Status</label>
                    <div className="mt-1">{getStatusBadge(selectedBondForDetails.status)}</div>
                  </div>
                  {selectedBondForDetails.approvalDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Approval Date</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedBondForDetails.approvalDate)}</p>
                    </div>
                  )}
                  {selectedBondForDetails.subscriptionStartDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Subscription Start Date</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedBondForDetails.subscriptionStartDate)}</p>
                    </div>
                  )}
                  {selectedBondForDetails.subscriptionEndDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Subscription End Date</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedBondForDetails.subscriptionEndDate)}</p>
                    </div>
                  )}
                  {selectedBondForDetails.issueDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Issue Date</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedBondForDetails.issueDate)}</p>
                    </div>
                  )}
                  {selectedBondForDetails.maturityDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Maturity Date</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedBondForDetails.maturityDate)}</p>
                    </div>
                  )}
                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Off-chain Bidders</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBondForDetails.offChainBiddersEntered} bidders</p>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            {selectedBondForDetails.couponRate && (
              <div className="mt-6">
                <h4 className="pb-2 mb-4 text-lg font-medium text-gray-900 border-b">Financial Information</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Coupon Rate</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBondForDetails.couponRate}%</p>
                  </div>
                  {selectedBondForDetails.currency && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Currency</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedBondForDetails.currency}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Approval Notes */}
            {selectedBondForDetails.approvalNotes && (
              <div className="mt-6">
                <h4 className="pb-2 mb-4 text-lg font-medium text-gray-900 border-b">Approval Notes</h4>
                <div className="p-4 rounded-lg bg-gray-50">
                  <p className="text-sm text-gray-700">{selectedBondForDetails.approvalNotes}</p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end pt-4 mt-6 space-x-3 border-t">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
              {(selectedBondForDetails.status === BOND_STATUS.APPROVED ||
                selectedBondForDetails.status === SUBSCRIPTION_STATUS.APPROVED_FOR_SUBSCRIPTION ||
                selectedBondForDetails.status === SUBSCRIPTION_STATUS.ALLOCATING_SUBSCRIBERS) && (
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleSetSubscriptionPeriod(selectedBondForDetails);
                    }}

                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Set Subscription Period
                  </button>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-lg animate-fade-in">
            <h3 className="mb-4 text-lg font-medium text-gray-900">{confirmAction.title}</h3>
            <p className="mb-6 text-sm text-gray-700">{confirmAction.message}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmAction.onConfirm) {
                    confirmAction.onConfirm();
                  }
                  setConfirmAction(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white rounded bg-primary-600 hover:bg-primary-700"
                style={{ backgroundColor: '#24295f' }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.open && (
        <div className="fixed top-4 right-4 z-[1000] animate-fade-in">
          <div className={`rounded-lg shadow-lg p-4 flex items-center space-x-3 min-w-[300px] ${toast.type === 'success' ? 'bg-green-50 border border-green-200' :
            toast.type === 'error' ? 'bg-red-50 border border-red-200' :
              'bg-blue-50 border border-blue-200'
            }`}>
            {toast.type === 'success' && <CheckCircle className="flex-shrink-0 w-5 h-5 text-green-600" />}
            {toast.type === 'error' && <XCircle className="flex-shrink-0 w-5 h-5 text-red-600" />}
            <p className={`text-sm font-medium ${toast.type === 'success' ? 'text-green-800' :
              toast.type === 'error' ? 'text-red-800' :
                'text-blue-800'
              }`}>
              {toast.message}
            </p>
            <button
              onClick={() => setToast({ open: false, message: '', type: 'success' })}
              className="ml-auto text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notification.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[999]">
          <div className="w-full max-w-sm p-5 mx-4 bg-white rounded-lg shadow-lg animate-fade-in">
            <div className="flex items-start">
              {(() => {
                const iconClasses = 'w-5 h-5 mr-3 mt-0.5 flex-shrink-0';
                switch (notification.type) {
                  case 'success':
                    return <CheckCircle className={`${iconClasses} text-green-600`} />;
                  case 'error':
                    return <XCircle className={`${iconClasses} text-red-600`} />;
                  case 'warning':
                    return <XCircle className={`${iconClasses} text-yellow-600`} />;
                  default:
                    return <InfoIcon className={`${iconClasses} text-blue-600`} />;
                }
              })()}
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">{notification.title}</h4>
                <p className="mt-1 text-sm text-gray-700 whitespace-pre-line">{notification.message}</p>
                <div className="flex justify-end mt-4 space-x-2">
                  <button
                    onClick={() => setNotification(n => ({ ...n, open: false }))}
                    className="px-3 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  >Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Fallback icon for info notifications
const InfoIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export default SubscriptionManagement; 